import { flowDefaultAction, flowEventRules } from './flow.js';
const RESUME_DEPTH_LIMIT = 64; // guard against a pathwayOf cycle
/** Redirect depth via recursive `pathwayOf`, cycle- and missing-parent-safe. */
function computeCol(id, byId, memo, seen) {
    const cached = memo.get(id);
    if (cached !== undefined)
        return cached;
    const t = byId.get(id);
    const parentId = t?.pathwayOf;
    if (!parentId || !byId.has(parentId) || seen.has(id) || seen.size > RESUME_DEPTH_LIMIT) {
        memo.set(id, 0);
        return 0;
    }
    seen.add(id);
    const col = 1 + computeCol(parentId, byId, memo, seen);
    seen.delete(id);
    memo.set(id, col);
    return col;
}
/** One rung's actions: its own, plus every per-block row when it is split. */
function sensorActions(rung) {
    if (!rung)
        return [];
    const rows = rung.split ? (rung.rules ?? []).map((r) => ({ action: r.action, cat: 'sensor' })) : [];
    return [{ action: rung.action, cat: 'sensor' }, ...rows];
}
/** Every routing action a task's flow can emit, with provenance: the source event's category (so an
 * edge's colour/terminator/label follow the event that made it) + rule title/id where applicable. */
function flowActions(flow) {
    if (!flow)
        return [];
    return [
        { action: flow.allCorrectAction, cat: 'route' },
        { action: flowDefaultAction(flow), cat: 'fallback' },
        { action: flow.noAnswerAction, cat: 'gate' },
        // The two sensor rungs, which emitted no edges at all — a redirect away from a dead sensor
        // simply did not appear on the board. A SPLIT rung routes per block, so every distinct row
        // action is its own edge alongside the rung's own.
        ...sensorActions(flow.noSensorData),
        ...sensorActions(flow.poorSensorData),
        ...flowEventRules(flow).map((r) => ({ action: r.action, label: r.title, ruleId: r.id, cat: 'gate', special: true })),
    ];
}
export function deriveGraph(tasks) {
    const byId = new Map(tasks.map((t) => [t.id, t]));
    // Group = the sequence a task belongs to: the spine (pathwayOf absent) or one detour lane
    // (a given pathwayOf value). Array order within a group is the sequence.
    const groupKey = (t) => (t.pathwayOf && byId.has(t.pathwayOf) ? `p:${t.pathwayOf}` : 'spine');
    const groups = new Map();
    for (const t of tasks) {
        const k = groupKey(t);
        (groups.get(k) ?? groups.set(k, []).get(k)).push(t);
    }
    const spine = (groups.get('spine') ?? []).map((t) => t.id);
    const colOf = {};
    const memo = new Map();
    const orphans = [];
    for (const t of tasks) {
        colOf[t.id] = computeCol(t.id, byId, memo, new Set());
        if (t.pathwayOf && !byId.has(t.pathwayOf))
            orphans.push(t.id);
    }
    const detoursOf = {};
    for (const t of tasks) {
        if (t.pathwayOf && byId.has(t.pathwayOf))
            (detoursOf[t.pathwayOf] ??= []).push(t.id);
    }
    // A "head" is any task some goto points at — it STARTS a pathway. Within one detour lane (same
    // pathwayOf) there can be several independent pathways (e.g. two remediations off the same task,
    // each triggered by a different condition); they're separated at each head so one doesn't flow
    // into the next. (The spine is always one continuous sequence, so heads don't split it.)
    const heads = new Set();
    for (const t of tasks) {
        for (const a of flowActions(t.flow)) {
            if (a.action.type === 'goto' && a.action.targetTaskId !== null)
                heads.add(a.action.targetTaskId);
        }
    }
    // "next" resolution: the following task in the same lane. At a lane's end (or when the following
    // task starts a different pathway) the sequence simply ENDS — a branch does NOT auto-resume to
    // the spine. A branch that should return wires an explicit goto on its last task; an un-wired
    // branch end is a deliberate-or-forgotten dead-end, which the builder draws with an End cap. See
    // project memory `project_experience_builder_connection_model` ("no auto-return; no silent
    // dead-ends"). (Previously a detour end recursively resumed at the task it hung off — dropped, as
    // that made a multi-task branch orphan tasks authored after the first.)
    function sequentialNext(id) {
        const t = byId.get(id);
        if (!t)
            return null;
        const group = groups.get(groupKey(t)) ?? [];
        const idx = group.findIndex((g) => g.id === id);
        const following = group[idx + 1];
        if (!following)
            return null;
        // Pathway boundary: the fallthrough is scoped to a remediation pathway (pathway). Crossing
        // into / out of / between pathways ends the sequence — a no-return task at an pathway's edge
        // dead-ends (End cap) instead of leaking into the next pathway. The pathway owns the boundary,
        // so within one pathway the head-split is ignored (a task may legitimately continue into a
        // head — a second entrance to the same remediation).
        const tPath = t.pathwayId ?? null;
        const fPath = following.pathwayId ?? null;
        if (tPath !== fPath)
            return null;
        if (tPath !== null)
            return following.id;
        // Both un-pathwayd → today's behavior: a detour lane splits at each head (independent pathways).
        const isDetour = !!(t.pathwayOf && byId.has(t.pathwayOf));
        if (isDetour && heads.has(following.id))
            return null;
        return following.id;
    }
    // Gather each task's flow actions with provenance, then turn them into edges.
    const rawEdges = [];
    for (const t of tasks) {
        const flow = t.flow;
        if (!flow) {
            // No flow authored yet → default forward continue (the correct through-line).
            const to = sequentialNext(t.id);
            if (to)
                rawEdges.push({ fromId: t.id, toId: to, kind: 'loose', cat: 'route' });
            continue;
        }
        for (const { action, label, ruleId, cat, special } of flowActions(flow)) {
            if (action.type === 'stay' || action.type === 'end')
                continue; // retry-in-place / deliberate End — no edge
            if (action.type === 'next') {
                const to = sequentialNext(t.id);
                if (to)
                    rawEdges.push({ fromId: t.id, toId: to, kind: 'loose', label, ruleId, cat, special });
            }
            else if (action.type === 'goto') {
                if (action.targetTaskId === null)
                    rawEdges.push({ fromId: t.id, toId: null, kind: 'loose-thread', label, ruleId, cat, special });
                else
                    rawEdges.push({ fromId: t.id, toId: action.targetTaskId, kind: 'locked', label, ruleId, cat, special });
            }
        }
    }
    // De-dupe by (from, to, kind): several conditions that all "continue" collapse to one loose
    // connector; the same locked target from two rules is one connector. Keep the first label seen.
    const edges = [];
    const seenEdge = new Set();
    for (const e of rawEdges) {
        const key = `${e.fromId}>${e.toId ?? 'Ø'}:${e.kind}`;
        if (seenEdge.has(key))
            continue;
        seenEdge.add(key);
        edges.push(e);
    }
    const nextOf = {};
    for (const t of tasks)
        nextOf[t.id] = sequentialNext(t.id);
    return { spine, colOf, detoursOf, edges, looseThreads: edges.filter((e) => e.kind === 'loose-thread'), orphans, heads: [...heads], nextOf };
}
/* ── Branch depth ────────────────────────────────────────────────────────────────────────────
 *
 * How deep a task sits in the redirect columns, and how deep a set of tasks would land.
 *
 * The number already existed twice — privately as `computeCol` here, and again as the `col`
 * recursion in the builder's own layout — and the cap was compared against the layout's copy at
 * three call sites while three other paths that create nesting ignored it entirely
 * (`handleWireToNewBranch`, `handleMakeBranchOf`, and the paste endpoint). A rule enforced in half
 * the places it applies is not a rule, so this is the one definition.
 *
 * ## The cap is soft, on purpose
 *
 * `MAX_BRANCH_COL` is a VIEW budget, not a truth about content: each level costs a full card width
 * and the board scroll-snaps per column on a phone. Past it, affordances are simply not offered —
 * the same way the builder's deeper "Branch of" drop zones already just don't appear. Nothing
 * refuses or rewrites data that is already deeper, because a presentation constraint has no
 * business deleting content, and because a teacher who reaches for a sixth level is evidence about
 * what this cap should be. A hard refusal would destroy that evidence.
 *
 * `ABSURD_BRANCH_COL` is the separate, genuinely hard bound: past it, depth is a cycle or corrupt
 * data rather than an authoring choice, and no view will ever render it usefully.
 */
/** Deepest redirect column the board offers to create. Spine = 0, a branch = 1, a branch-of-branch = 2… */
export const MAX_BRANCH_COL = 4;
/** Beyond an authoring choice — a cycle or corruption. The one depth a write may be refused for. */
export const ABSURD_BRANCH_COL = 20;
/** A task's redirect column: 0 on the spine, 1 in a branch, 2 in a branch of a branch. */
export function branchDepth(tasks, taskId) {
    const byId = new Map(tasks.map((t) => [t.id, t]));
    return computeCol(taskId, byId, new Map(), new Set());
}
/**
 * The deepest nesting WITHIN a set, counting only parents that are also in the set.
 *
 * `0` for a flat set. Clipboard tasks carry `pathwayOf` values pointing at their source ids, so
 * measuring against the set itself is what makes this meaningful before the paste exists: a
 * pathway whose members all hang off one head is flat; one containing a branch-of-branch is 1.
 */
export function internalDepth(tasks) {
    const ids = new Set(tasks.map((t) => t.id));
    // Only parents inside the set count, so a reference out of the set reads as a root.
    const local = tasks.map((t) => (t.pathwayOf && ids.has(t.pathwayOf) ? t : { ...t, pathwayOf: undefined }));
    const byId = new Map(local.map((t) => [t.id, t]));
    const memo = new Map();
    let deepest = 0;
    for (const t of local)
        deepest = Math.max(deepest, computeCol(t.id, byId, memo, new Set()));
    return deepest;
}
/**
 * Whether a set pasted as a branch off a host at `hostCol` would stay inside the cap.
 *
 * The head lands one column past the host, so the deepest member ends at
 * `hostCol + 1 + internalDepth`. Written as a sum rather than `hostCol + 1` because a copied
 * pathway can itself contain a branch-of-branch — any check phrased as "+1" is wrong for exactly
 * the payloads most worth checking.
 */
export function fitsBranchDepth(hostCol, incoming, cap = MAX_BRANCH_COL) {
    return hostCol + 1 + internalDepth(incoming) <= cap;
}
/* ── Terminals ───────────────────────────────────────────────────────────────────────────────
 *
 * Only a BRANCH or the LAST spine task may end the experience. The Flow panel enforces that by
 * disabling the control (`correctAllowEnd`), which is display-only — it never rewrites what is
 * stored. So an `end` authored legitimately on the last spine task stayed put when that task
 * stopped being last, planting a terminator mid-spine that the panel would not have let anyone
 * author and that nothing downstream corrected. Students would simply stop there.
 *
 * `pasteTasks` already handled its own version of this, by handing a locked correct to the last
 * pasted task. Every OTHER way a task changes position — dragging it up the spine, outdenting a
 * branch onto it — had no equivalent, because each site had to remember. That is the bug class, so
 * this is applied where snapshots enter the history stack rather than at each call site.
 */
/** Whether this task is allowed to author `end` on its correct through-line. */
export function mayEndExperience(tasks, taskId) {
    const { spine } = deriveGraph(tasks);
    const at = spine.indexOf(taskId);
    // Not on the spine at all → a branch, which may always end. On it → only the last one may.
    return at === -1 || at === spine.length - 1;
}
/**
 * Demote any `end` that its task is no longer entitled to, back to `next`.
 *
 * Idempotent, and returns the SAME array when nothing changes so callers can keep referential
 * equality checks. Only the correct through-line is touched: `end` is not offered on any other
 * event, so an `end` elsewhere is not something position can invalidate.
 */
export function normalizeTerminals(tasks) {
    const { spine } = deriveGraph(tasks);
    const last = spine[spine.length - 1];
    const onSpine = new Set(spine);
    let changed = false;
    const out = tasks.map((t) => {
        if (t.flow?.allCorrectAction.type !== 'end')
            return t;
        if (!onSpine.has(t.id) || t.id === last)
            return t;
        changed = true;
        return { ...t, flow: { ...t.flow, allCorrectAction: { type: 'next' } } };
    });
    return changed ? out : tasks;
}
/**
 * Where each selected task hangs, once the selection is lifted out of its board.
 *
 * A multi-task selection can cross lanes — that is the point of it: take a spine task together with
 * the pathways hanging off it. But a selection is also allowed to be sparse, and a sparse one can
 * strand a task: select a spine task, skip the pathway below it, and take a pathway that hangs off a
 * task INSIDE that skipped pathway. The stranded task's parent is not travelling, so it has nothing
 * to hang off in the destination.
 *
 * The rule is to snap across the missing layer to the next closest relative: climb `pathwayOf` until
 * a task that IS selected turns up, and hang off that. Nothing selected above it makes it a root,
 * and roots land wherever the paste is aimed.
 *
 * Resolved HERE, at copy time, and not at paste time — because climbing past a skipped ancestor
 * needs that ancestor's own parent, which only the board knows. A clipboard holding just the chosen
 * tasks could not climb past the first gap. So the clipboard stores the ANSWER: every member's
 * `pathwayOf` either names another member or is null, and the paste has nothing left to work out.
 *
 * Snapping only ever shortens a chain, so a selection can never paste deeper than it was.
 *
 * Same rule `internalDepth` applies when it measures a set — a parent outside the set reads as a
 * root — stated once so measuring and placing cannot disagree.
 */
export function resolveSelectionParents(allTasks, selectedIds) {
    const selected = new Set(selectedIds);
    const parentOf = new Map(allTasks.map((t) => [t.id, t.pathwayOf ?? null]));
    const out = new Map();
    for (const id of selected) {
        // `seen` guards a cycle in stored data, as `computeCol` does: a self-referential chain must not
        // hang a copy.
        const seen = new Set([id]);
        let cursor = parentOf.get(id) ?? null;
        let found = null;
        while (cursor && !seen.has(cursor)) {
            if (selected.has(cursor)) {
                found = cursor;
                break;
            }
            seen.add(cursor);
            cursor = parentOf.get(cursor) ?? null;
        }
        out.set(id, found);
    }
    return out;
}
/**
 * The tasks a student can arrive at MORE THAN ONCE — those lying on a cycle of routing edges.
 *
 * Several controls only mean anything on such a task: whether attempts count per visit or per
 * session, what a `visits` condition compares against, whether answers survive a return. On a
 * linear experience every one of them is inert, and an inert control is worse than an absent one
 * — it invites a teacher to reason about a difference that cannot occur.
 *
 * A task is re-enterable exactly when there is a path from it back to itself: a `goto` that loops
 * back, a detour that returns to the task it hung off, or any longer ring. Read from the derived
 * edges rather than from the stored flow, so a route expressed as `next` into a detour's return
 * counts the same as one written as an explicit `goto`.
 *
 * A LOOSE THREAD IS NOT A LOOP. An unresolved goto has no destination yet, so it cannot carry
 * anyone anywhere; counting it would turn on these controls for a route the teacher has not
 * finished authoring, and turn them off again when they did.
 */
export function reenterableTasks(graph) {
    const out = new Map();
    for (const e of graph.edges) {
        if (e.toId === null)
            continue;
        (out.get(e.fromId) ?? out.set(e.fromId, []).get(e.fromId)).push(e.toId);
    }
    const loops = new Set();
    for (const start of out.keys()) {
        // Breadth-first from each task's SUCCESSORS, so arriving back at `start` means a real cycle
        // rather than the trivial zero-length one.
        const seen = new Set();
        const queue = [...(out.get(start) ?? [])];
        while (queue.length > 0) {
            const id = queue.shift();
            if (id === start) {
                loops.add(start);
                break;
            }
            if (seen.has(id))
                continue;
            seen.add(id);
            queue.push(...(out.get(id) ?? []));
        }
    }
    return loops;
}
//# sourceMappingURL=graph.js.map